import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// A dealt spread, dealt.
//
// The cards start as a stack beside the layout, go out to their places in the
// order the spread asks for, and land face down. Turning one over is the
// reader's move, not the page's — which is the whole reason a spread has an
// order at all.
//
// The layout is ONE svg, the same reason the Tree is: the browser scales the
// coordinate system, so nothing here has to know how wide the page is, and the
// Tree of Life spread can sit on the tradition's own coordinates untouched.
// The clarifying cards are in that svg too, which is what lets them deal out
// of the same pack with the same travel and the same turn.

// Reduced motion means no dealing animation and no flip — the cards are simply
// where they belong. It does NOT mean skipping the face-down step: that is the
// feature, not decoration.
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return reduced;
}

const STACK_GUTTER = 4;
// How far the clarifier row stands clear of the spread, and where along the
// spread's width it begins: above the others and off a little to the right,
// growing rightward in the order the cards were drawn.
const CLARIFIER_GAP = 5;
const CLARIFIER_PITCH = 2;
const CLARIFIER_START = 0.55;
// Room above the row for its captions, which are outside each card.
const CLARIFIER_CAPTION = 3;

// A CLOSER LOOK, AFTER THE READER HAS STAYED ON ONE CARD.
//
// Long enough that the pointer crossing cards on its way to the one it wants —
// the Celtic Cross lays six of them overlapping — does not set the spread
// flinching, and no longer than that.
//
// TWO WAITS AND ONE MECHANISM, which is the opposite way round from how this
// started. The cards and the clarifier button's tooltip shared one number
// while they genuinely waited the same time; the card's wait is shorter now,
// so the number is two numbers and each says which thing it belongs to. What
// stays shared is `useDwell`: the duration is a judgment about one control and
// the cancellable timer is not.
const CARD_DWELL_MS = 1333;
const HELP_DWELL_MS = 2000;
// TWICE THE SIZE, because the point of it is to let somebody READ the card —
// the artwork, the small figures in a pip card's border — and not merely to
// mark which one the pointer is on. The ring and the panel already say that.
const DWELL_SCALE = 2.25;

function parseBox(viewBox) {
  const [x, y, width, height] = viewBox.split(/\s+/).map(Number);
  return { x, y, width, height };
}

// Where the clarifiers stand, and how far right the last one reaches.
function clarifierRow(viewBox, cardWidth, cardHeight, count) {
  const box = parseBox(viewBox);
  const pitch = cardWidth + CLARIFIER_PITCH;
  const left = box.x + box.width * CLARIFIER_START;
  const centreY = box.y - CLARIFIER_GAP - cardHeight / 2;
  return {
    right: left + cardWidth + Math.max(count - 1, 0) * pitch,
    pointAt: (index) => ({ x: left + cardWidth / 2 + index * pitch, y: centreY })
  };
}

// The spread's own box is tight around the cards, because that is the picture.
// The pack has to stand somewhere, so the client opens a gutter to its left;
// clarifiers open a band above it and, once there are enough of them, a little
// room to the right. Both are derived rather than stored: a spread added later
// gets them for free, which is the whole reason the geometry lives in the
// payload and the staging lives here.
function outerBox(viewBox, cardWidth, cardHeight, clarifierCount) {
  const box = parseBox(viewBox);
  // ROOM FOR A CARD THAT GROWS. The outermost svg clips to its own viewport,
  // so a card at the edge of the layout would have the part that just got
  // bigger cut off — and four of the Celtic Cross's ten sit against an edge.
  // DERIVED from DWELL_SCALE rather than written out, so the room and the zoom
  // cannot disagree when the zoom is retuned.
  //
  // IT IS PERMANENT, AND AT TWICE THE SIZE THAT COSTS SOMETHING: half a card
  // on each side, so the spread is drawn a little smaller than the bare layout
  // all the time. Opened only while a card is big it would rescale the whole
  // diagram the moment one grew, sliding every other card out from under the
  // pointer mid-look — which is worse than a diagram that is consistently a
  // few per cent smaller. Letting the card escape with `overflow: visible`
  // was the other candidate and is worse again: at twice the size it reaches
  // far enough past the top edge to land on the toolbar's own buttons.
  const padX = (cardWidth * (DWELL_SCALE - 1)) / 2;
  const padY = (cardHeight * (DWELL_SCALE - 1)) / 2;

  const left = box.x - cardWidth - STACK_GUTTER - padX;
  const bottom = box.y + box.height + padY;
  let top = box.y - padY;
  let right = box.x + box.width + padX;

  if (clarifierCount > 0) {
    top = box.y - CLARIFIER_GAP - cardHeight - CLARIFIER_CAPTION - padY;
    const row = clarifierRow(viewBox, cardWidth, cardHeight, clarifierCount);
    right = Math.max(right, row.right + 1 + padX);
  }

  return { value: `${left} ${top} ${right - left} ${bottom - top}` };
}

// Halfway down the SPREAD, so the pack reads as beside the layout rather than
// as a card that missed its place — and measured against the spread rather
// than against the whole box, so that drawing a clarifier, which opens a band
// above, does not slide the pack out from under the cards still sitting on it.
function stackPoint(viewBox, cardWidth) {
  const box = parseBox(viewBox);
  return { x: box.x - cardWidth / 2 - STACK_GUTTER / 2, y: box.y + box.height / 2 };
}

// A CANCELLABLE WAIT, shared by the two things here that wait: a card the
// reader is staying on, and the clarifier button's tooltip. They wait the same
// two seconds for the same reason — a pointer on its way somewhere else should
// not set the page moving or talking — so the duration is one constant and the
// mechanism is one hook rather than two timers free to drift apart.
function useDwell() {
  const timer = useRef(null);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const after = useCallback((ms, run) => {
    cancel();
    timer.current = setTimeout(run, ms);
  }, [cancel]);

  // Returned as a pair rather than an object: both halves are stable, so
  // anything depending on them is too, which an object literal rebuilt every
  // render would quietly spoil.
  useEffect(() => cancel, [cancel]);
  return [after, cancel];
}

// The lattice for decks with no surviving reverse. Defined once per svg and
// referenced by every card that needs it, rather than redrawn per card: ten
// cards' worth of hatching is ten times the nodes for the same picture.
function AgedBackPattern() {
  return (
    <defs>
      <pattern id="spread-aged" width="2.4" height="2.4" patternUnits="userSpaceOnUse"
               patternTransform="rotate(45)">
        <path className="spread__back-hatch" d="M0 0V2.4M0 0H2.4" />
        <circle className="spread__back-speck" cx="1.2" cy="1.2" r="0.22" />
      </pattern>
    </defs>
  );
}

// The order it goes out in, upper left, on both faces — so the spread can be
// read as a sequence before a single card is turned.
//
// IT GOES WHILE THE CARD IS BIG, and comes back when the card shrinks. The
// plate sits on the artwork, which is the one thing the zoom exists to show.
// Wrapped in a group of its own so ONE rule fades both halves of it; faded
// rather than unmounted, because the number is what tells a reader which
// position a face-down card is and it has to be there again the moment the
// pointer leaves, without a repaint.
//
// A clarifier gets none: it has no place in the spread's sequence, and a second
// "1" beside the first card's would say it had one. Its caption, outside the
// card, is what names it.
function Ordinal({ badge, width, height }) {
  if (!badge) return null;
  return (
    <g className="spread__ordinal-plate">
      <circle className="spread__ordinal" cx={-width / 2 + 2.6} cy={-height / 2 + 2.6} r={2.4} />
      <text className="spread__ordinal-text" x={-width / 2 + 2.6} y={-height / 2 + 3.5}
            textAnchor="middle">
        {badge}
      </text>
    </g>
  );
}

function CardBack({ deck, width, height, badge, backTestid }) {
  return (
    <>
      {deck.back ? (
        <image
          href={deck.back.path}
          x={-width / 2}
          y={-height / 2}
          width={width}
          height={height}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        // A deck whose own reverse cannot be shipped gets the generic one:
        // aged stock and a lattice, which is what an unmarked eighteenth-century
        // back actually looked like. It is deliberately nobody's design, so it
        // cannot be mistaken for a scan of the real thing.
        <g data-testid={backTestid}>
          <rect className="spread__back-stock" x={-width / 2} y={-height / 2}
                width={width} height={height} rx={1} />
          <rect className="spread__back-lattice" x={-width / 2} y={-height / 2}
                width={width} height={height} rx={1} fill="url(#spread-aged)" />
          <rect className="spread__back-rule" x={-width / 2 + 1.1} y={-height / 2 + 1.1}
                width={width - 2.2} height={height - 2.2} rx={0.7} fill="none" />
          <rect className="spread__back-rule spread__back-rule--inner"
                x={-width / 2 + 2} y={-height / 2 + 2}
                width={width - 4} height={height - 4} rx={0.5} fill="none" />
        </g>
      )}

      <rect className="spread__card-edge" x={-width / 2} y={-height / 2}
            width={width} height={height} rx={1} fill="none" />

      <Ordinal badge={badge} width={width} height={height} />
    </>
  );
}

function CardFace({ card, reversed, badge, width, height }) {
  return (
    <>
      {card.image ? (
        <image
          href={card.image.path}
          x={-width / 2}
          y={-height / 2}
          width={width}
          height={height}
          preserveAspectRatio="xMidYMid slice"
          /* A reversed card is turned end for end, which is what reversed
             MEANS — not a flag printed beside an upright picture. */
          transform={reversed ? "rotate(180)" : undefined}
        />
      ) : (
        <>
          <rect className="spread__card-blank" x={-width / 2} y={-height / 2}
                width={width} height={height} rx={1} />
          <text className="spread__card-title" x={0} y={0} textAnchor="middle">
            {card.title}
          </text>
        </>
      )}

      <rect className="spread__card-edge" x={-width / 2} y={-height / 2}
            width={width} height={height} rx={1} fill="none" />

      <Ordinal badge={badge} width={width} height={height} />

      {reversed && (
        <text className="spread__reversed" x={0} y={height / 2 + 2.6} textAnchor="middle">
          reversed
        </text>
      )}
    </>
  );
}

// ONE CARD, WHEREVER IT IS GOING. The slot says where it lands, what it is
// called and what badge it wears; this knows only how to travel there and
// turn. A clarifier is the same card in a different place, which is why there
// is no second component for one.
function Card({ slot, deck, spread, dealt, faceUp, selected, flipping, dwelling,
                onDwell, onSelect }) {
  const { cardWidth: width, cardHeight: height } = spread;
  const stack = stackPoint(spread.viewBox, width);

  // Undealt cards sit on the stack; dealt ones are at their place. One
  // transform carries both, so the deal is a transition rather than a
  // re-render in a different spot.
  // Undealt cards sit a hair apart so the pack reads as a pack. Card 1 is drawn
  // last and so lies on top, which is the card that leaves first.
  const lean = slot.lean * 0.14;
  const at = dealt ? slot.at : { x: stack.x + lean, y: stack.y - lean };
  const spin = dealt && slot.rotated ? " rotate(90deg)" : "";
  // The scale goes LAST, after the translate, so it grows about the card's own
  // centre: every shape in a card is drawn from -width/2, so the translated
  // origin already IS that centre and no transform-origin is needed. Written
  // before the translate it would have moved the card as well as grown it.
  const zoom = dwelling ? ` scale(${DWELL_SCALE})` : "";
  // A CSS transform, not the presentation attribute: the attribute cannot be
  // transitioned, and the deal IS the transition — which the zoom then rides
  // for free, and which reduced motion already turns off for both.
  const transform = `translate(${at.x}px, ${at.y}px)${spin}${zoom}`;

  const label = faceUp
    ? `${slot.name}: ${slot.card.title}${slot.reversed ? ", reversed" : ""}`
    : `${slot.name}: face down. Activate to turn it over.`;

  return (
    <g
      className={[
        "spread__card",
        slot.clarifier ? "spread__card--clarifier" : "",
        dealt ? "spread__card--dealt" : "spread__card--stacked",
        faceUp ? "spread__card--face-up" : "spread__card--face-down",
        flipping ? "spread__card--flipping" : "",
        // NOT a class of its own: nothing styles a selected card any more, and
        // a class styled by nothing is the dead rule `.archangels__header`
        // already taught this repository to delete rather than keep. What a
        // selected card still carries is `aria-pressed`, which is the part a
        // screen reader was ever reading.
        dwelling ? "spread__card--dwelling" : ""
      ].filter(Boolean).join(" ")}
      style={{ transform }}
      role="button"
      tabIndex={dealt ? 0 : -1}
      aria-pressed={selected}
      aria-label={label}
      /* A card still on the stack is not a card anybody is looking at, so the
         dwell only starts once it is down. */
      onMouseEnter={() => dealt && onDwell(slot.key)}
      onMouseLeave={() => onDwell(null)}
      onClick={() => dealt && onSelect(slot.key)}
      onKeyDown={(event) => {
        if (!dealt || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        onSelect(slot.key);
      }}
      data-testid={slot.testid}
      data-face={faceUp ? "up" : "down"}
      data-dealt={dealt ? "true" : "false"}
      data-rotated={dealt && slot.rotated ? "true" : "false"}
      data-dwelling={dwelling ? "true" : "false"}
    >
      {/* Both faces exist; which one shows is the flip. Swapping the
          element out instead would lose the card mid-turn. */}
      <g className="spread__side spread__side--back">
        <CardBack deck={deck} width={width} height={height} badge={slot.badge}
                  backTestid={`spread-aged-back-${slot.backKey}`} />
      </g>
      <g className="spread__side spread__side--face">
        <CardFace card={slot.card} reversed={slot.reversed} badge={slot.badge}
                  width={width} height={height} />
      </g>

      {/* A clarifier is captioned OUTSIDE the card, because it has no position
          in the spread to be named by and an unlabelled card above the layout
          reads as one that missed its place. The caption is text and not a
          colour, which is the rule the nav tab and the segmented control
          follow for the same reason. */}
      {slot.caption && (
        <text className="spread__clarifier-caption" x={0} y={-height / 2 - 1.2}
              textAnchor="middle">
          {slot.caption}
        </text>
      )}
    </g>
  );
}

// WHAT A CLARIFIER IS, in this study's own words and labelled as such. The
// cards' meanings are Waite's; this sentence is the site saying what the
// control it just offered actually did.
const CLARIFIER_NOTE =
  "A card from the pack still in hand, drawn after the spread to resolve what " +
  "it left unclear. It answers the reading as a whole rather than any one " +
  "place in it.";

function Detail({ slot, deck, faceUp }) {
  if (!slot) return null;

  if (!faceUp) {
    return (
      <aside className="reading__detail" aria-live="polite" data-testid="reading-detail">
        <p className="reading__ordinal">{slot.kicker}</p>
        <h2 className="reading__position">{slot.heading}</h2>
        <p className="reading__position-meaning">{slot.meaning}</p>
        <hr className="reading__rule" />
        <p className="reading__absent" data-testid="detail-face-down">
          This card is still face down. Turn it over to read it.
        </p>
      </aside>
    );
  }

  const { card, reversed } = slot;

  return (
    <aside className="reading__detail" aria-live="polite" data-testid="reading-detail">
      <p className="reading__ordinal">{slot.kicker}</p>
      <h2 className="reading__position">{slot.heading}</h2>
      <p className="reading__position-meaning">{slot.meaning}</p>

      <hr className="reading__rule" />

      <div className="reading__card-head">
        {card.image && (
          <img className="reading__card-image" src={card.image.path} alt={card.image.alt}
               style={reversed ? { transform: "rotate(180deg)" } : undefined} />
        )}
        <div>
          <p className="reading__card-number">{card.numberLabel}</p>
          <h3 className="reading__card-title" data-testid="detail-title">{card.title}</h3>
          {card.esotericTitle && <p className="reading__card-esoteric">{card.esotericTitle}</p>}
          {card.title !== card.filedAs && (
            <p className="reading__card-filed">Filed here as {card.filedAs}</p>
          )}
          {reversed && (
            <p className="reading__card-reversed" data-testid="detail-reversed">Drawn reversed</p>
          )}
        </div>
      </div>

      <p className="reading__summary">{card.summary}</p>

      {card.picture && (
        <section className="reading__block">
          <h4>What the card shows</h4>
          <p>{card.picture}</p>
        </section>
      )}

      {/* The meaning that MATCHES the orientation it was drawn in. Showing the
          upright reading of a reversed card is the one thing this must not do. */}
      {reversed ? (
        <section className="reading__block" data-testid="detail-meaning">
          <h4>Divinatory meaning, reversed</h4>
          {card.reversedMeaning ? (
            <p>{card.reversedMeaning}</p>
          ) : (
            <p className="reading__absent">
              {deck.slug === "rider_waite"
                ? "Waite prints no reversed reading for this card."
                : `The ${deck.name} deck publishes no divinatory readings.`}
            </p>
          )}
        </section>
      ) : (
        <section className="reading__block" data-testid="detail-meaning">
          <h4>Divinatory meaning</h4>
          {card.upright ? (
            <p>{card.upright}</p>
          ) : (
            <p className="reading__absent">
              The {deck.name} deck publishes no divinatory readings, so none is shown.
            </p>
          )}
        </section>
      )}

      {card.goldenDawn && (
        <section className="reading__block">
          <h4>Book T{card.goldenDawnTitle ? ` · ${card.goldenDawnTitle}` : ""}</h4>
          <p>{card.goldenDawn}</p>
        </section>
      )}

      <p className="reading__more">
        <a href={card.path}>Open this card's full research page →</a>
      </p>
    </aside>
  );
}

const DEAL_INTERVAL = 260;
const FLIP_DURATION = 320;

export default function Reading({ reading: dealt }) {
  // THE READING IS STATE, because a clarifier is drawn into the one already on
  // the table. The alternative is a full page load, which re-deals the spread
  // and takes every card the reader had turned back down — the answer they
  // were reading, closed in order to add to it.
  const [reading, setReading] = useState(dealt);
  const { spread, deck, positions, clarifiers, provenance } = reading;
  const reduced = usePrefersReducedMotion();

  // How many have been dealt so far. Everything else follows from it.
  const [dealtCount, setDealtCount] = useState(0);
  const [clarifiersDealt, setClarifiersDealt] = useState(0);
  const [faceUp, setFaceUp] = useState(() => new Set());
  const [flipping, setFlipping] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [helpState, setHelpState] = useState("closed");
  const help = helpState === "open";
  const [dwelling, setDwelling] = useState(null);
  const timers = useRef([]);
  const [waitForCard, stopCardWait] = useDwell();
  const [waitForHelp, stopHelpWait] = useDwell();

  // Deal on arrival, one card at a time, in the spread's own order.
  useEffect(() => {
    setDealtCount(0);
    setClarifiersDealt(0);
    setFaceUp(new Set());
    setSelected(null);

    if (reduced) {
      setDealtCount(positions.length);
      return undefined;
    }

    timers.current.forEach(clearTimeout);
    timers.current = positions.map((_, index) =>
      setTimeout(() => setDealtCount(index + 1), DEAL_INTERVAL * (index + 1))
    );
    return () => timers.current.forEach(clearTimeout);
  }, [reading.draw, spread.slug, deck.slug, positions.length, reduced]);

  const dealing = dealtCount < positions.length;

  // A clarifier WAITS FOR THE SPREAD, because a card arriving on top of nine
  // still travelling reads as part of the deal rather than as an answer to it.
  // Drawn into a finished reading the wait is already over, so it leaves the
  // stack at once. This is keyed off `reading.draw` only through the effect
  // above, which resets the count — the draw does not change when a clarifier
  // is added, and that is exactly why the spread does not re-deal.
  useEffect(() => {
    if (clarifiersDealt >= clarifiers.length) return undefined;
    if (reduced) {
      setClarifiersDealt(clarifiers.length);
      return undefined;
    }
    if (dealing) return undefined;
    const timer = setTimeout(() => setClarifiersDealt((count) => count + 1), DEAL_INTERVAL);
    return () => clearTimeout(timer);
  }, [clarifiersDealt, clarifiers.length, reduced, dealing]);

  // EVERY CARD ON THE TABLE, in one list, because the only thing that differs
  // between a position and a clarifier is where it lands and what names it.
  // Two lists would be two chances for them to behave differently.
  const slots = useMemo(() => {
    const row = clarifierRow(spread.viewBox, spread.cardWidth, spread.cardHeight,
                             clarifiers.length);

    return [
      ...positions.map((position) => ({
        key: `p${position.number}`,
        testid: `spread-card-${position.number}`,
        backKey: position.number,
        clarifier: false,
        badge: String(position.number),
        caption: null,
        lean: position.number - 1,
        at: { x: position.x, y: position.y },
        rotated: position.rotated,
        card: position.card,
        reversed: position.reversed,
        name: `Position ${position.number}, ${position.positionName}`,
        kicker: `Card ${position.number} of the spread`,
        heading: position.positionName,
        meaning: position.positionMeaning
      })),
      ...clarifiers.map((clarifier, index) => ({
        key: `c${clarifier.ordinal}`,
        // NOT `spread-card-`: the browser suite counts the spread's cards with
        // a prefix match on that, so a clarifier wearing it would be counted
        // as a position the spread does not have.
        testid: `clarifier-card-${clarifier.ordinal}`,
        backKey: `c${clarifier.ordinal}`,
        clarifier: true,
        badge: null,
        caption: `clarifier ${clarifier.ordinal}`,
        lean: positions.length + index,
        at: row.pointAt(index),
        rotated: false,
        card: clarifier.card,
        reversed: clarifier.reversed,
        name: `Clarifying card ${clarifier.ordinal}`,
        kicker: `Clarifier ${clarifier.ordinal}`,
        heading: "Drawn to clarify",
        meaning: CLARIFIER_NOTE
      }))
    ];
  }, [positions, clarifiers, spread]);

  // A slot's ordinal is the number in its key, so one predicate answers for
  // both lists without either having to carry a second copy of its index.
  const isOut = (slot) => (slot.clarifier
    ? Number(slot.key.slice(1)) <= clarifiersDealt
    : Number(slot.key.slice(1)) <= dealtCount);

  const turn = (key) => {
    setSelected(key);
    if (faceUp.has(key)) return;

    setFaceUp((current) => new Set(current).add(key));
    if (reduced) return;

    setFlipping((current) => new Set(current).add(key));
    setTimeout(() => {
      setFlipping((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }, FLIP_DURATION);
  };

  // THE DWELL IS THE PARENT'S, so only one card can be waiting at a time: a
  // timer per card would leave the one the pointer has left still counting
  // down, and two cards would grow at once.
  const dwell = useCallback((key) => {
    if (key === null) {
      stopCardWait();
      setDwelling(null);
      return;
    }
    waitForCard(CARD_DWELL_MS, () => setDwelling(key));
  }, [waitForCard, stopCardWait]);

  // THE TOOLTIP WAITS TOO, for the reason a card does: a pointer crossing the
  // toolbar on its way to the spread should not set the page talking. It waits
  // LONGER than a card now, which is deliberate — a card growing under the
  // pointer is what the reader was reaching for, and a panel of prose opening
  // over the toolbar is not. FOCUS DOES NOT WAIT — a reader who has tabbed to the
  // button has arrived at it, which is the distinction the card dwell draws
  // too — and a screen reader never waited either way, because
  // `aria-describedby` reads the text whether the bubble is drawn or not.
  const openHelpAfterDwell = useCallback(() => {
    setHelpState("waiting");
    waitForHelp(HELP_DWELL_MS, () => setHelpState("open"));
  }, [waitForHelp]);

  const openHelpNow = useCallback(() => {
    stopHelpWait();
    setHelpState("open");
  }, [stopHelpWait]);

  const closeHelp = useCallback(() => {
    stopHelpWait();
    setHelpState("closed");
  }, [stopHelpWait]);

  // DISMISSIBLE WITHOUT MOVING THE POINTER, which is what WCAG 1.4.13 asks of
  // anything that appears on hover — so the key is listened for on the
  // DOCUMENT and not on the button. On the button it only ever worked when the
  // button had focus, which is exactly the case hover is not. It also cancels
  // a wait still running, or Escape would be answered two seconds later by the
  // thing it had just dismissed.
  useEffect(() => {
    if (helpState === "closed") return undefined;
    const onKey = (event) => { if (event.key === "Escape") closeHelp(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [helpState, closeHelp]);

  const turnAll = () => {
    setFaceUp(new Set(slots.map((slot) => slot.key)));
    if (selected === null) setSelected(slots[0]?.key ?? null);
  };

  // DRAWING A CLARIFIER IS A REQUEST, because the pack is in the database and
  // the card has to end up in the address. So the button asks the server the
  // same `clarify` question the no-script link asks, follows the redirect it
  // answers with, and takes BOTH the landed address and the new payload out of
  // the reply — the pattern the natal chart already uses to recalculate in
  // place. Pushing the landed address rather than one built here means the
  // link always says what the server actually drew.
  const drawClarifier = useCallback(() => {
    if (drawing || !reading.clarifiable) return;
    setDrawing(true);

    const asked = new URL(window.location.href);
    asked.searchParams.set("clarify", "1");

    fetch(asked.toString(), { headers: { Accept: "text/html" } })
      .then((response) => response.text().then((html) => [html, response.url]))
      .then(([html, landed]) => {
        const node = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector("#tarot-reading");
        if (!node) return;
        window.history.pushState({}, "", landed);
        setReading(JSON.parse(node.dataset.reading));
      })
      .finally(() => setDrawing(false));
  }, [drawing, reading.clarifiable]);

  // Once the first card is down there is something to read about it, even face
  // down: the POSITION is knowable before the card is. Falling back to it means
  // the panel is never empty while cards are on the table.
  const showing = selected ?? (dealtCount > 0 ? slots[0]?.key : null);
  const current = slots.find((slot) => slot.key === showing) || null;
  const turnedCount = faceUp.size;

  // SVG HAS NO Z-INDEX, so a card that grows where it stands grows UNDER its
  // neighbours — and the Celtic Cross lays six of them overlapping, which is
  // exactly where the eye is when one of them is being looked at. The dwelling
  // card is painted LAST. It is MOVED rather than drawn twice, so there is
  // still one hit target and one tab stop: the answer the Tree's selected path
  // reaches from the other direction, where a repaint was cheaper than moving
  // a path out of the layer its colour belongs to.
  const painted = useMemo(() => {
    // Reversed so card 1 ends up on top of the stack, which is the card that
    // leaves it first. The clarifiers fall before the spread for the same
    // reason: drawn last, so deepest in the pack while they are still on it.
    const order = [...slots].reverse();
    if (dwelling === null) return order;
    const at = order.findIndex((slot) => slot.key === dwelling);
    if (at < 0) return order;
    return [...order.slice(0, at), ...order.slice(at + 1), order[at]];
  }, [slots, dwelling]);

  const backCredit = useMemo(() => {
    if (!deck.back) return null;
    return [deck.back.attribution, deck.back.license].filter(Boolean).join(" · ");
  }, [deck]);

  const clarifyHelp = reading.clarifiable
    ? "Draws the top card of the pack still in hand, to resolve whatever the reading left unclear. It lands above the spread, face down, and travels in the link with the rest."
    : `A reading carries at most ${reading.maxClarifiers} clarifying cards, so there is no room for another.`;

  return (
    <div className="reading">
      <div className="reading__layout">
        <div className="reading__toolbar">
          <button
            type="button"
            className="reading__turn-all"
            onClick={turnAll}
            disabled={dealing || turnedCount === slots.length}
            data-testid="turn-all"
          >
            {turnedCount === slots.length ? "All turned over" : "Turn them all over"}
          </button>
          <span className="reading__counter" data-testid="turned-count">
            {dealing
              ? `Dealing… ${dealtCount} of ${positions.length}`
              : `${turnedCount} of ${slots.length} turned over`}
          </span>

          {/* UP AND TO THE SIDE, which is where a thing you do AFTER reading
              belongs: not under the spread where it would read as part of the
              deal, and not beside "turn them all over" as though the two were
              the same kind of move. */}
          <span className="reading__clarify"
                onMouseEnter={openHelpAfterDwell}
                onMouseLeave={closeHelp}>
            <button
              type="button"
              className="reading__clarify-button"
              onClick={drawClarifier}
              onFocus={openHelpNow}
              onBlur={closeHelp}
              disabled={dealing || drawing || !reading.clarifiable}
              aria-describedby="clarify-help"
              data-testid="draw-clarifier"
            >
              {drawing ? "Drawing…" : "Draw a Clarifying Card"}
            </button>
            {/* THE DESCRIPTION IS ALWAYS IN THE DOM and only its visibility
                moves. Rendered on hover alone it would describe nothing for
                every reader not using a mouse, and `aria-describedby` pointing
                at a node React had unmounted would describe nothing for
                anybody — which is the half-implemented pattern this site
                refuses to announce. */}
            <span className="reading__clarify-help" id="clarify-help" role="tooltip"
                  data-open={help ? "true" : "false"} data-state={helpState}
                  data-testid="clarify-help">
              {clarifyHelp}
            </span>
          </span>
        </div>

        <svg
          className="reading__svg"
          viewBox={outerBox(spread.viewBox, spread.cardWidth, spread.cardHeight,
                            clarifiers.length).value}
          preserveAspectRatio="xMidYMid meet"
          role="group"
          aria-label={`${spread.name}, ${positions.length} cards from the ${deck.name} deck${
            clarifiers.length ? `, with ${clarifiers.length} clarifying` : ""
          }${clarifiers.length === 1 ? " card" : clarifiers.length ? " cards" : ""}`}
        >
          <AgedBackPattern />
          {painted.map((slot) => (
            <Card
              key={slot.key}
              slot={slot}
              deck={deck}
              spread={spread}
              dealt={isOut(slot)}
              faceUp={faceUp.has(slot.key)}
              flipping={flipping.has(slot.key)}
              selected={slot.key === showing}
              dwelling={slot.key === dwelling}
              onDwell={dwell}
              onSelect={turn}
            />
          ))}
        </svg>

        <div className="reading__notes">
          {provenance && <p className="reading__provenance" data-testid="provenance">{provenance}</p>}
          {clarifiers.length > 0 && (
            <p className="reading__provenance" data-testid="clarifier-provenance">
              {clarifiers.length === 1 ? "The clarifying card was" : "The clarifying cards were"} taken
              from the top of the pack still in hand — the cards not on the table, mixed
              again by the same method. Nothing is stored, so the shuffle that dealt the
              spread does not survive the reply it was sent in.
            </p>
          )}
          {backCredit && (
            <p className="reading__back-credit" data-testid="back-credit">
              Card back: {backCredit}.
            </p>
          )}
          {deck.backNote && (
            <p className="reading__back-note" data-testid="back-note">{deck.backNote}</p>
          )}
        </div>
      </div>

      <Detail slot={current} deck={deck}
              faceUp={current ? faceUp.has(current.key) : false} />
    </div>
  );
}
